import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CalendarDays, Search, Printer, FileSpreadsheet, Filter,
  ClipboardList, BookOpen, Users, FileBarChart, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { AREA_OPTIONS, getAreaLabel } from "@/lib/areaOptions";
import { usePagination } from "@/lib/usePagination";

// ─── Costanti module-scope (evitano riallocazioni ad ogni render) ───
const MONTH_OPTIONS = Object.freeze([
  { value: "all", label: "Tutti" },
  { value: "01", label: "Gennaio" }, { value: "02", label: "Febbraio" },
  { value: "03", label: "Marzo" }, { value: "04", label: "Aprile" },
  { value: "05", label: "Maggio" }, { value: "06", label: "Giugno" },
  { value: "07", label: "Luglio" }, { value: "08", label: "Agosto" },
  { value: "09", label: "Settembre" }, { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" }, { value: "12", label: "Dicembre" },
]);

// ─── Hook debounce reale (07/07/2026 v4 — perf ricerca Cliente 360°) ───
// Piccola utility inline: aspetta `delay` ms di quiete prima di
// propagare il valore. Evita di eseguire il filtro sui 10K+ record
// mentre l'utente sta digitando. Complementare a `useDeferredValue`.
function useDebouncedValue(value, delay = 180) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Sottocomponente ricerca ISOLATO (v5 — 07/07/2026) ────────────────
//
//  RATIO. Il parent RegistroAttivitaTab genera ~1000 righe di JSX per
//  render; anche con memoizzazione delle sotto-parti, il costo di
//  reconciliazione al singolo keystroke era ~150-170ms — inaccettabile
//  su dataset reali (10K+ righe).
//
//  Fix definitivo: l'input NON è più controllato dal parent. Ha stato
//  locale isolato (`local`) che aggiorna il DOM istantaneamente ad ogni
//  keystroke SENZA far ri-renderizzare il parent. Solo dopo `delay`ms di
//  quiete (default 180) chiama `onDebounced(value)` propagando il valore
//  al parent → un unico ricalcolo di searchIndex/visibleItems/render.
//
//  Sync inverso: se il parent resetta il valore (Reset filtri), il prop
//  `externalValue` cambia e viene riflesso in local. Confronto con `ref`
//  per evitare loop infiniti.
//
//  React.memo garantisce che il component NON ri-renderizza per motivi
//  non-search (es. cambio filterMonth/filterYear del parent).
const DebouncedSearchInput = React.memo(function DebouncedSearchInput({
  externalValue,
  onDebounced,
  placeholder,
  delay = 180,
  className,
  testId,
}) {
  const [local, setLocal] = useState(externalValue || "");
  const timerRef = useRef(null);
  const lastSentRef = useRef(externalValue || "");

  // Sync-down: se il parent resetta o cambia programmaticamente il
  // valore (es. tramite `resetFilters`), rifletti nel locale — ma solo
  // quando è davvero un cambiamento esterno, non un eco del nostro debounce.
  useEffect(() => {
    if (externalValue !== lastSentRef.current) {
      setLocal(externalValue || "");
      lastSentRef.current = externalValue || "";
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    }
  }, [externalValue]);

  // Cleanup: se il componente viene smontato mentre un debounce è pending.
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleChange = useCallback((e) => {
    const v = e.target.value;
    setLocal(v);                             // aggiorna DOM istantaneo
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      lastSentRef.current = v;
      onDebounced(v);                        // propaga UNA SOLA VOLTA al parent
    }, delay);
  }, [delay, onDebounced]);

  return (
    <Input
      value={local}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      data-testid={testId}
    />
  );
});

/**
 * Registro Attività — P1 (18/02/2026).
 *
 * Vista AGGREGATA READ-ONLY su `daily_practices` (Lavoro Quotidiano) +
 * `client_contacts` (Diario Giornaliero). Non c'è una collection
 * separata: la pagina interroga `GET /api/activity-log` e renderizza
 * il risultato. Caso d'uso primario: preparare la fatturazione mensile
 * per un cliente.
 *
 * Architettura:
 *  - filtri identici a Diario/Lavoro Quotidiano (label-sopra in Card p-3);
 *  - toggle "Sorgenti" per scegliere Lavoro / Diario / Entrambe;
 *  - modalità Dettaglio (lista cronologica per cliente) o Panoramica
 *    (groupBy=client, multi-cliente);
 *  - Stampa + Esporta Excel sempre sulla vista corrente filtrata;
 *  - props.initialClientId / props.initialClientName: deep-link da
 *    "Anagrafica Clienti" (entry-point dalla scheda cliente).
 */

const todayISO = () => new Date().toISOString().slice(0, 10);

function formatDateIT(iso) {
  if (!iso) return "";
  // Accept both YYYY-MM-DD and ISO timestamp.
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

// Range presets identici a Diario/Lavoro.
function periodRange(period, customFrom, customTo) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  switch (period) {
    case "oggi":      return { from: iso(today), to: iso(today) };
    case "ieri": {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { from: iso(y), to: iso(y) };
    }
    case "7giorni": {
      const s = new Date(today); s.setDate(s.getDate() - 6);
      return { from: iso(s), to: iso(today) };
    }
    case "15giorni": {
      const s = new Date(today); s.setDate(s.getDate() - 14);
      return { from: iso(s), to: iso(today) };
    }
    case "mese": {
      const s = new Date(today); s.setDate(s.getDate() - 29);
      return { from: iso(s), to: iso(today) };
    }
    case "custom":   return { from: customFrom || "", to: customTo || "" };
    default:         return { from: "", to: "" };
  }
}

function RegistroAttivitaTab({
  clients = [],
  operators = [],
  operatorRole = "amministratore",
  initialClientId = "",
  initialClientName = "",
  onClearInitialClient,
}) {
  // Permission scope correction (20/02/2026): operatori Studio/Agenzia
  // possono ora accedere al Registro, ma vedono SOLO i record della
  // propria area gestionale. Il backend rinforza il vincolo lato
  // Default soft management filter (20/02/2026 — revisione collaborativa).
  // Studio/Agenzia non sono più barriere rigide: pre-impostano il
  // filtro al login per UX, ma l'operatore può ampliarlo manualmente
  // (es. vedere anche l'altra area per collaborazione cross-team).
  const isStudioOperator = operatorRole === "operatore_studio";
  const isAgenziaOperator = operatorRole === "operatore_agenzia";
  // `mgmtLocked` mantenuto solo per backward-compat — ora sempre false.
  // Il select Gestione resta sempre abilitato.
  const forcedMgmt = isStudioOperator ? "studio" : isAgenziaOperator ? "agenzia" : "";
  const mgmtLocked = false;

  // ─── Filtri ───
  const [clientId, setClientId] = useState(initialClientId || "");
  // Refinement 01/07/2026 (Cliente 360°): filtro periodo trasformato da
  // preset "oggi/ieri/7gg/mese" a coppia (Anno, Mese) — più naturale per
  // l'uso quotidiano. Manteniamo "custom" per ricerche puntuali.
  const [filterMode, setFilterMode] = useState("month"); // "month" | "custom"
  const currentYear = new Date().getFullYear();
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [filterMonth, setFilterMonth] = useState("all"); // "all" | "01".."12"
  // Retro-compat: filterPeriod/filterFrom/filterTo alimentano periodRange
  // esistente. Inizializzati LAZY con i valori corretti dell'anno corrente
  // per evitare la doppia chiamata API in mount (06/07/2026 — perf fix).
  const [filterPeriod, setFilterPeriod] = useState("custom");
  const [filterFrom, setFilterFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [filterTo, setFilterTo] = useState(() => `${new Date().getFullYear()}-12-31`);
  // Filtro Cliente eliminato completamente dalla UI (06/07/2026). Lo stato
  // `clientId` resta solo per supportare i deep-link da Anagrafica → si
  // apre il modulo pre-filtrato, con indicatore rimovibile nell'header.
  const [filterMgmt, setFilterMgmt] = useState(forcedMgmt);
  const [filterArea, setFilterArea] = useState("");
  const [filterOperator, setFilterOperator] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sourceLavoro, setSourceLavoro] = useState(true);
  const [sourceDiario, setSourceDiario] = useState(true);
  const [sourceScadenze, setSourceScadenze] = useState(true);
  const [groupBy, setGroupBy] = useState("none"); // "none" | "client"
  const [searchQuery, setSearchQuery] = useState("");
  // Perf ricerca (v5 — 07/07/2026):
  //  L'Input NON è più controllato dal parent. `DebouncedSearchInput`
  //  gestisce internamente lo stato del testo e chiama `setSearchQuery`
  //  SOLO dopo 180ms di quiete → il parent ri-renderizza al massimo
  //  ~5 volte al secondo invece di ~60. Non serve più `useDeferredValue`
  //  perché il debounce interno del sottocomponente è già sufficiente.

  // Cliente 360°: quando filterMode="month", calcoliamo from/to a partire
  // da anno/mese senza toccare periodRange (compat). Se month="all",
  // copre l'intero anno selezionato.
  useEffect(() => {
    if (filterMode !== "month") return;
    const y = parseInt(filterYear, 10);
    if (!Number.isFinite(y)) return;
    if (filterMonth === "all") {
      setFilterFrom(`${y}-01-01`);
      setFilterTo(`${y}-12-31`);
    } else {
      const mi = parseInt(filterMonth, 10);
      const lastDay = new Date(y, mi, 0).getDate();
      const mm = String(mi).padStart(2, "0");
      setFilterFrom(`${y}-${mm}-01`);
      setFilterTo(`${y}-${mm}-${String(lastDay).padStart(2, "0")}`);
    }
    setFilterPeriod("custom"); // periodRange userà from/to raw
  }, [filterMode, filterYear, filterMonth]);

  // Nome del cliente selezionato (definito più sotto — usato dal chip header)
  // Anni disponibili (ultimi 5 + prossimo)
  const YEAR_OPTIONS = useMemo(() => {
    const y0 = new Date().getFullYear();
    return [y0 + 1, y0, y0 - 1, y0 - 2, y0 - 3, y0 - 4].map(String);
  }, []);

  // Se il ruolo cambia (re-login con account diverso) ri-applichiamo il
  // vincolo, così non si "trascina" un filtro libero in una sessione
  // operatore.
  useEffect(() => {
    if (forcedMgmt && filterMgmt !== forcedMgmt) {
      setFilterMgmt(forcedMgmt);
    }
  }, [forcedMgmt, filterMgmt]);

  // ─── Data ───
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  // Sync da deep-link (entry point dalla scheda cliente).
  useEffect(() => {
    if (initialClientId && initialClientId !== clientId) {
      setClientId(initialClientId);
      setGroupBy("none"); // dettaglio cliente
    }
  }, [initialClientId, clientId]);

  const range = useMemo(
    () => periodRange(filterPeriod, filterFrom, filterTo),
    [filterPeriod, filterFrom, filterTo]
  );

  const sourcesParam = useMemo(() => {
    const s = [];
    if (sourceLavoro) s.push("lavoro");
    if (sourceDiario) s.push("diario");
    if (sourceScadenze) s.push("scadenze");
    return s.join(",");
  }, [sourceLavoro, sourceDiario, sourceScadenze]);

  // ─── Fetch ───
  const load = useCallback(async () => {
    if (!sourcesParam) {
      toast.error("Seleziona almeno una sorgente (Lavoro o Diario)");
      return;
    }
    setLoading(true);
    try {
      const data = await api.getActivityLog({
        clientId: clientId || undefined,
        fromDate: range.from || undefined,
        toDate: range.to || undefined,
        managementType: filterMgmt || undefined,
        area: filterArea || undefined,
        operatorId: filterOperator || undefined,
        status: filterStatus || undefined,
        sources: sourcesParam,
        groupBy: groupBy === "client" ? "client" : undefined,
      });
      setResponse(data);
    } catch (e) {
      toast.error(`Errore caricamento: ${e.message || e}`);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, range, filterMgmt, filterArea, filterOperator, filterStatus, sourcesParam, groupBy]);

  // Auto-load on mount + on any filter change (debounced via React batching).
  useEffect(() => { load(); }, [load]);

  // ─── Client-side filtering: ricerca testo libero (v4 — 07/07/2026) ───
  //
  //  BOTTLENECK STORICO (v3 e precedenti):
  //  Per ogni keystroke `visibleItems` iterava sui 10.000+ record e per
  //  ognuno creava un array di 13 stringhe, lo filtrava, joinava, poi
  //  toLowerCase() → ~130K string ops per digitazione. Sulla nostra macchina
  //  di test ~40-80ms bloccando il thread principale e il typing.
  //
  //  FIX v4: pre-calcoliamo UNA SOLA VOLTA (memoizzato su `response`) un
  //  array `searchIndex[i]` di stringhe già concatenate e lowercased.
  //  L'operazione .toLowerCase(), che è la più costosa (allocazione di
  //  nuove stringhe), avviene una tantum al fetch, non più a ogni tasto.
  //  Loop for classico (non .map) per minimizzare l'overhead.
  //  Il separatore \u0001 previene falsi match ai confini tra i campi
  //  senza costo di memoria significativo.
  const searchIndex = useMemo(() => {
    const items = response?.items;
    if (!items || items.length === 0) return null;
    const idx = new Array(items.length);
    const SEP = "\u0001";
    for (let i = 0; i < items.length; i++) {
      const r = items[i];
      idx[i] = (
        (r.clientName || "") + SEP +
        (r.description || "") + SEP +
        (r.area || "") + SEP +
        (r.operatorName || "") + SEP +
        (r.practiceReference || "") + SEP +
        (r.practiceNumber || "") + SEP +
        (r.practiceTitle || "") + SEP +
        (r.quoteReference || "") + SEP +
        (r.quoteNumber || "") + SEP +
        (r.invoiceReference || "") + SEP +
        (r.invoiceNumber || "") + SEP +
        (r.notes || "") + SEP +
        (r.note || "")
      ).toLowerCase();
    }
    return idx;
  }, [response]);

  const visibleItems = useMemo(() => {
    const items = response?.items;
    if (!items) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q || !searchIndex) return items;
    // Filter O(N) con singola .includes() su stringa già preparata.
    // ~5-10ms su 10K record vs 40-80ms della vecchia implementazione.
    const out = [];
    for (let i = 0; i < items.length; i++) {
      if (searchIndex[i].includes(q)) out.push(items[i]);
    }
    return out;
  }, [response, searchQuery, searchIndex]);

  // ─── Totali "visibili" (v4 — 07/07/2026) ─────────────────────────────
  // Aggregati calcolati SUL DATASET STAMPATO/VISIBILE, non su quello
  // server. Sostituiscono `response.totals` per:
  //  - header UI (contatore, spese, fatturabili) — così la ricerca
  //    libera aggiorna correttamente i contatori;
  //  - footer stampa (riepilogo del periodo) — unica sorgente di verità
  //    condivisa, elimina ricalcoli duplicati.
  // Costo: O(N) semplice loop for su visibleItems (~1-2ms su 10K).
  const visibleTotals = useMemo(() => {
    const items = visibleItems;
    const byArea = {};
    let count = 0, expensesAmount = 0, expensesPaid = 0, expensesUnpaid = 0;
    let billableCount = 0, billableExpenses = 0;
    for (let i = 0; i < items.length; i++) {
      const r = items[i];
      count++;
      const amt = r.expensesAmount || 0;
      expensesAmount += amt;
      if (r.expensesPaid) expensesPaid += amt; else expensesUnpaid += amt;
      if (r.status === "chiusa") {
        billableCount++;
        billableExpenses += amt;
      }
      const areaKey = r.area || "—";
      const bucket = byArea[areaKey] || (byArea[areaKey] = { count: 0, expensesAmount: 0 });
      bucket.count++;
      bucket.expensesAmount += amt;
    }
    return { count, expensesAmount, expensesPaid, expensesUnpaid,
             billableCount, billableExpenses, byArea };
  }, [visibleItems]);

  const visibleGroups = useMemo(() => response?.groups || [], [response]);

  // Paginazione globale (default 50, selettore 10/30/50/100, jump):
  // due istanze indipendenti per Panoramica vs Dettaglio, ognuna con
  // la propria preferenza persistita.
  const {
    paged: pagedGroups,
    paginationBar: groupsPaginationBar,
  } = usePagination(visibleGroups, {
    storageKey: "registro-attivita-panoramica",
    itemLabelSingular: "cliente",
    itemLabelPlural: "clienti",
  });
  const {
    paged: pagedItems,
    paginationBar: itemsPaginationBar,
  } = usePagination(visibleItems, {
    storageKey: "registro-attivita-dettaglio",
    itemLabelSingular: "voce",
    itemLabelPlural: "voci",
  });

  // ─── Helpers ───
  const resetFilters = useCallback(() => {
    setClientId("");
    // Cliente 360° (01/07/2026): reset a Anno corrente + Mese "Tutti"
    const y = new Date().getFullYear();
    setFilterMode("month");
    setFilterYear(String(y));
    setFilterMonth("all");
    setFilterFrom(`${y}-01-01`);
    setFilterTo(`${y}-12-31`);
    setFilterPeriod("custom");
    // Operatori Studio/Agenzia: il filtro Gestione resta bloccato
    // sulla loro area; gli admin tornano a "Tutte".
    setFilterMgmt(forcedMgmt);
    setFilterArea("");
    setFilterOperator("");
    setFilterStatus("");
    setSourceLavoro(true);
    setSourceDiario(true);
    setSourceScadenze(true);
    setGroupBy("none");
    setSearchQuery("");
    if (onClearInitialClient) onClearInitialClient();
  }, [forcedMgmt, onClearInitialClient]);

  const hasActiveFilters = useMemo(() => (
    !!clientId ||
    filterMode !== "month" ||
    filterMonth !== "all" ||
    filterYear !== String(new Date().getFullYear()) ||
    (filterMgmt && filterMgmt !== forcedMgmt) ||
    !!filterArea ||
    !!filterOperator ||
    !!filterStatus ||
    !sourceLavoro ||
    !sourceDiario ||
    !sourceScadenze ||
    groupBy === "client" ||
    !!searchQuery.trim()
  ), [clientId, filterMode, filterMonth, filterYear, filterMgmt, forcedMgmt,
      filterArea, filterOperator, filterStatus, sourceLavoro, sourceDiario,
      sourceScadenze, groupBy, searchQuery]);

  const selectedClientName = useMemo(() => {
    if (!clientId) return "";
    const c = clients.find((x) => x.id === clientId);
    return c?.name || initialClientName || "";
  }, [clientId, clients, initialClientName]);

  // ─── Export & Print ───
  const buildFilterSummary = useCallback(() => {
    const parts = [];
    if (selectedClientName) parts.push(`Cliente: ${selectedClientName}`);
    if (range.from || range.to) parts.push(`Periodo: ${formatDateIT(range.from) || "…"} → ${formatDateIT(range.to) || "…"}`);
    if (filterMgmt) parts.push(`Gestione: ${filterMgmt === "studio" ? "Studio" : "Agenzia"}`);
    if (filterArea) parts.push(`Area: ${filterArea}`);
    if (filterOperator) {
      const o = operators.find((x) => x.id === filterOperator);
      if (o) parts.push(`Operatore: ${o.fullName || o.name}`);
    }
    if (filterStatus) parts.push(`Stato: ${filterStatus === "chiusa" ? "Chiuse (fatturabili)" : "Aperte"}`);
    const srcs = [sourceLavoro && "Lavoro", sourceDiario && "Diario", sourceScadenze && "Scadenze"].filter(Boolean).join(" + ");
    parts.push(`Sorgenti: ${srcs}`);
    if (searchQuery.trim()) parts.push(`Cerca: "${searchQuery.trim()}"`);
    return parts.join(" · ");
  }, [selectedClientName, range, filterMgmt, filterArea, filterOperator,
      filterStatus, operators, sourceLavoro, sourceDiario, sourceScadenze,
      searchQuery]);

  const handleExportExcel = useCallback(() => {
    try {
      let rows = [];
      if (groupBy === "client") {
        rows = visibleGroups.map((g) => ({
          Cliente: g.clientName,
          "N. Attività": g.count,
          "Ultima Attività": formatDateIT(g.lastActivityDate),
          "Spese Tot. €": g.expensesAmount.toFixed(2),
          "Spese Pagate €": g.expensesPaid.toFixed(2),
          // Anteprima fatturazione (P3): voci chiuse = "fatturabili"
          // secondo la semantica P1 del Registro.
          "Fatturabili (n.)": g.billableCount ?? 0,
          "Fatturabili (spese €)": (g.billableExpenses ?? 0).toFixed(2),
        }));
      } else {
        rows = visibleItems.map((r) => ({
          Data: formatDateIT(r.date),
          Sorgente: r.sourceLabel,
          Cliente: r.clientName,
          Gestione: r.managementType === "studio" ? "Studio" : (r.managementType === "agenzia" ? "Agenzia" : ""),
          Area: r.area,
          Operatore: r.operatorName,
          Stato: r.status === "chiusa" ? "Chiuso" : "Aperto",
          Fatturabile: r.status === "chiusa" ? "Sì" : "",
          Descrizione: (r.description || "").replace(/[\r\n]+/g, " ").trim(),
          "Spese €": r.expensesAmount ? r.expensesAmount.toFixed(2) : "",
          Pagate: r.expensesAmount ? (r.expensesPaid ? "Sì" : "No") : "",
        }));
      }
      if (rows.length === 0) {
        toast.info("Nessuna attività da esportare con i filtri correnti");
        return;
      }
      const headers = Object.keys(rows[0]);
      const escape = (v) => {
        const s = String(v ?? "");
        return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [
        headers.join(";"),
        ...rows.map((r) => headers.map((h) => escape(r[h])).join(";")),
      ].join("\r\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const slug = (selectedClientName || "tutti").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
      a.href = url;
      a.download = `cliente_360_${slug}_${todayISO()}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Esportate ${rows.length} righe`);
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    }
  }, [groupBy, visibleGroups, visibleItems, selectedClientName]);

  const handlePrint = useCallback(() => {
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) { toast.error("Apertura finestra di stampa bloccata dal browser"); return; }
    const esc = (s) => String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<":"&lt;",">":"&gt;","&":"&amp;" }[ch]));
    const todayStr = new Date().toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

    // ─── Totali coerenti con le righe EFFETTIVAMENTE stampate ───
    // (07/07/2026 v4). In modalità cronologica riutilizziamo
    // `visibleTotals` (già memoizzato — nessun ricalcolo duplicato).
    // In modalità raggruppata sommiamo le metriche pre-aggregate
    // dei gruppi (dataset piccolo, calcolo inline economico).
    let totals;
    if (groupBy === "client") {
      totals = visibleGroups.reduce((acc, g) => {
        acc.count += g.count || 0;
        acc.expensesAmount += g.expensesAmount || 0;
        acc.expensesPaid += g.expensesPaid || 0;
        acc.billableCount += g.billableCount || 0;
        acc.billableExpenses += g.billableExpenses || 0;
        return acc;
      }, { count: 0, expensesAmount: 0, expensesPaid: 0, expensesUnpaid: 0,
           billableCount: 0, billableExpenses: 0, byArea: {} });
      totals.expensesUnpaid = totals.expensesAmount - totals.expensesPaid;
    } else {
      totals = visibleTotals;
    }
    const filterLine = buildFilterSummary();

    let bodyTable = "";
    if (groupBy === "client") {
      bodyTable = `
        <table>
          <thead><tr>
            <th>Cliente</th><th class="num">N. Attività</th><th>Ultima Attività</th>
            <th class="num">Spese Tot.</th><th class="num">Pagate</th>
            <th class="num">Fatturabili</th>
          </tr></thead>
          <tbody>
            ${visibleGroups.map(g => `<tr>
              <td>${esc(g.clientName)}</td>
              <td class="num">${g.count}</td>
              <td>${esc(formatDateIT(g.lastActivityDate))}</td>
              <td class="num">€ ${g.expensesAmount.toFixed(2)}</td>
              <td class="num">€ ${g.expensesPaid.toFixed(2)}</td>
              <td class="num">${g.billableCount ?? 0}${(g.billableExpenses ?? 0) > 0 ? ` · € ${(g.billableExpenses ?? 0).toFixed(2)}` : ""}</td>
            </tr>`).join("")}
          </tbody>
        </table>`;
    } else {
      // ─── Report cronologico DENSE PROFESSIONAL (07/07/2026 v3) ───
      // Riscritto come report ERP/CRM/help-desk: singola riga per
      // attività. La descrizione è nella STESSA riga in una colonna
      // wide (~50-60% pagina), gli altri metadata sono compressi con
      // ellipsis. Ogni attività = una <tr>, page-break-inside:avoid.
      // Obiettivo: 40-60 attività per pagina A4 senza sprecare carta.
      // Se filtriamo per singolo cliente, RIMUOVIAMO la colonna Cliente
      // e diamo tutto il suo spazio alla descrizione (da ~50% a ~62%).
      const isSingleClient = !!selectedClientName;
      const srcShort = (t) => t === "lavoro" ? "Lavoro" : t === "diario" ? "Diario" : t === "scadenza" ? "Scadenza" : "";

      const headCols = isSingleClient
        ? `<th class="c-date">Data</th>
           <th class="c-orig">Origine</th>
           <th class="c-area">Area</th>
           <th class="c-op">Operatore</th>
           <th class="c-stato">Stato</th>
           <th class="c-fat" title="Fatturabile">✓</th>
           <th>Descrizione</th>`
        : `<th class="c-date">Data</th>
           <th class="c-cliente">Cliente</th>
           <th class="c-orig">Origine</th>
           <th class="c-area">Area</th>
           <th class="c-op">Operatore</th>
           <th class="c-stato">Stato</th>
           <th class="c-fat" title="Fatturabile">✓</th>
           <th>Descrizione</th>`;

      bodyTable = `
        <table class="rpt">
          <thead><tr>${headCols}</tr></thead>
          <tbody>
            ${visibleItems.map(r => `<tr>
              <td class="c-date">${esc(formatDateIT(r.date))}</td>
              ${isSingleClient ? "" : `<td class="c-cliente" title="${esc(r.clientName)}">${esc(r.clientName || "—")}</td>`}
              <td class="c-orig o-${r.sourceType}">${esc(srcShort(r.sourceType) || r.sourceLabel || "")}</td>
              <td class="c-area" title="${esc(r.area)}">${esc(r.area || "—")}</td>
              <td class="c-op" title="${esc(r.operatorName)}">${esc(r.operatorName || "—")}</td>
              <td class="c-stato s-${r.status === "chiusa" ? "chiusa" : "aperta"}">${r.status === "chiusa" ? "Chiuso" : "Aperto"}</td>
              <td class="c-fat">${r.status === "chiusa" ? "&#10003;" : ""}</td>
              <td class="c-desc">${r.description ? esc(r.description) : '<span class="desc-empty">—</span>'}</td>
            </tr>`).join("")}
          </tbody>
        </table>`;
    }

    const byAreaRows = Object.entries(totals.byArea || {})
      .sort((a, b) => (b[1].count || 0) - (a[1].count || 0))
      .map(([area, v]) => `<li><strong>${esc(area)}</strong>: ${v.count} ${v.count === 1 ? "voce" : "voci"}${(v.expensesAmount || 0) > 0 ? ` · € ${v.expensesAmount.toFixed(2)}` : ""}</li>`)
      .join("");

    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"/>
      <title>Cliente 360°${selectedClientName ? ` — ${esc(selectedClientName)}` : ""}</title>
      <style>
        /* ═══════════════════════════════════════════════════════════
           STAMPA CLIENTE 360° — REPORT DENSE PROFESSIONAL v3
           Layout ispirato a report cronologici ERP/CRM/help-desk:
           singola riga per attività, descrizione wide 50-60% pagina
           con wrap naturale, metadata compressi con ellipsis.
           Obiettivo: 40-60 attività leggibili per pagina A4.
           ═══════════════════════════════════════════════════════════ */
        @page { size: A4 portrait; margin: 10mm 8mm; }
        *{box-sizing:border-box}
        html,body{margin:0;padding:0}
        body{font-family:'Segoe UI',-apple-system,Roboto,Arial,sans-serif;color:#0f172a;font-size:9.5px;line-height:1.35}
        .doc-header{padding:2px 0 6px;border-bottom:1.5px solid #334155;margin-bottom:6px}
        .doc-header h1{margin:0;font-size:13px;font-weight:700;color:#0f172a;letter-spacing:-0.2px}
        .doc-header h1 .client-name{color:#1d4ed8;font-weight:600}
        .doc-header .meta{color:#64748b;font-size:8.5px;margin-top:2px}
        .doc-header .filters{color:#475569;font-size:8.5px;font-style:italic;margin-top:1px;line-height:1.3}

        /* ─── Tabella report cronologico dense ─── */
        table.rpt{width:100%;border-collapse:collapse;table-layout:fixed;font-size:9px}
        table.rpt thead th{
          text-align:left;background:#f1f5f9;color:#334155;
          font-weight:600;font-size:7.5px;text-transform:uppercase;letter-spacing:0.05em;
          padding:3px 5px;border-bottom:1px solid #94a3b8;
          vertical-align:middle;
        }
        table.rpt tbody td{
          padding:3px 5px;vertical-align:top;
          border-bottom:0.5px solid #e5e7eb;
          overflow:hidden;
        }
        table.rpt tbody tr:nth-child(even) td{background:#fafbfc}
        table.rpt tbody tr:hover td{background:#f1f5f9}

        /* ─── Colonne (widths % ottimizzate per A4 194mm content) ─── */
        /* Multi-cliente: Data 7% · Cliente 14% · Origine 6% · Area 8% · Operatore 10% · Stato 5% · Fatt 3% · Descrizione 47% */
        /* Single-cliente (senza colonna Cliente): Descrizione ~61% */
        .c-date{width:7%;white-space:nowrap;font-weight:500;color:#1e293b;font-variant-numeric:tabular-nums}
        .c-cliente{width:14%;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;font-weight:600;color:#1d4ed8}
        .c-orig{width:6%;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;font-weight:600;font-size:8.5px}
        .c-orig.o-lavoro{color:#1d4ed8}
        .c-orig.o-diario{color:#b45309}
        .c-orig.o-scadenza{color:#7c3aed}
        .c-area{width:8%;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;color:#475569}
        .c-op{width:10%;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;color:#475569}
        .c-stato{width:5%;white-space:nowrap;font-weight:500;font-size:8.5px}
        .c-stato.s-chiusa{color:#166534}
        .c-stato.s-aperta{color:#64748b}
        .c-fat{width:3%;text-align:center;color:#16a34a;font-weight:700;font-size:11px}
        /* Descrizione: NON impostiamo width → prende il residuo (table-layout:fixed).
           Wrap naturale su parole; niente truncation. */
        .c-desc{color:#0f172a;word-wrap:break-word;overflow-wrap:break-word;white-space:pre-wrap;overflow:visible;line-height:1.4}
        .desc-empty{color:#cbd5e1}

        /* ─── Footer riepilogo ─── */
        .footer{margin-top:10px;padding-top:6px;border-top:1px solid #cbd5e1;font-size:8.5px;color:#475569;line-height:1.5}
        .footer h2{font-size:9.5px;margin:8px 0 3px;color:#0f172a;font-weight:600;text-transform:uppercase;letter-spacing:0.05em}
        .footer p{margin:2px 0}
        .footer ul{margin:2px 0;padding-left:14px}
        .footer strong{color:#1e293b}

        @media print{
          button{display:none}
          /* Ogni attività (una <tr>) non deve spezzarsi tra due pagine. */
          table.rpt tbody tr{page-break-inside:avoid;break-inside:avoid}
          /* Ripeti l'header su ogni pagina. */
          table.rpt thead{display:table-header-group}
          .footer{page-break-inside:avoid}
          /* Nessun hover in stampa (evita background chiaro casuale). */
          table.rpt tbody tr:hover td{background:transparent}
          table.rpt tbody tr:nth-child(even):hover td{background:#fafbfc}
        }
      </style></head><body>
      <div class="doc-header">
        <h1>Cliente 360°${selectedClientName ? ` — <span class="client-name">${esc(selectedClientName)}</span>` : ""}</h1>
        <div class="meta">Stampato il ${esc(todayStr)} · ${totals.count} voci</div>
        <div class="filters">${esc(filterLine)}</div>
      </div>
      ${bodyTable}
      <div class="footer">
        <h2>Riepilogo del periodo</h2>
        <p><strong>Voci:</strong> ${totals.count}${
          (totals.billableCount ?? 0) > 0
            ? ` · <strong>Fatturabili (chiuse):</strong> ${totals.billableCount}${(totals.billableExpenses ?? 0) > 0 ? ` (spese € ${(totals.billableExpenses ?? 0).toFixed(2)})` : ""}`
            : ""
        }</p>
        <p><strong>Spese totali:</strong> € ${totals.expensesAmount.toFixed(2)} ·
           <strong>pagate:</strong> € ${totals.expensesPaid.toFixed(2)} ·
           <strong>da pagare:</strong> € ${totals.expensesUnpaid.toFixed(2)}</p>
        ${byAreaRows ? `<p><strong>Per area:</strong></p><ul>${byAreaRows}</ul>` : ""}
      </div>
      <script>window.onload=()=>{setTimeout(()=>window.print(),250);}</script>
      </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  }, [groupBy, visibleGroups, visibleItems, visibleTotals, selectedClientName, buildFilterSummary]);

  // ─── Render ───
  // Header UI ora legge da `visibleTotals` (client-side, coerente con
  // ricerca libera). Fallback a zeri se dataset vuoto/non caricato.
  // `visibleTotals` è già memoizzato → nessun ricalcolo duplicato con
  // il footer stampa e i bottoni Stampa/Export usano `.length` degli
  // stessi array visibleItems/visibleGroups.
  const totals = visibleTotals;

  return (
    <div className="space-y-2" data-testid="registro-attivita-tab" data-module="attivita-module">
      {/* Header compact — v1.9.0 UX Operativa v2: icona + titolo + counter
          + azioni tutti su una riga, sottotitolo <p> ridotto a text-[11px].
          Recuperati ~30-40px verticali per far spazio alla timeline. */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-1 py-1" data-testid="attivita-module-header">
        <div className="flex items-center gap-3 flex-wrap">
          <span
            className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "var(--gis-green-50)" }}
          >
            <FileBarChart className="h-4 w-4" style={{ color: "var(--gis-green)" }} />
          </span>
          <div>
            <h2 className="gis-heading font-bold text-slate-900 text-lg leading-tight flex items-center gap-2 flex-wrap">
              Cliente 360°
              {clientId && (
                <span
                  className="inline-flex items-center gap-1 h-5 px-2 rounded border border-[var(--gis-green-200)] bg-[var(--gis-green-50)] text-[var(--gis-green-dark)] text-[10px] font-medium"
                  data-testid="ral-header-client-indicator"
                  title="Stai visualizzando le attività di un singolo cliente. Clicca × per vedere tutti."
                >
                  <span className="truncate max-w-[240px]">{selectedClientName || "—"}</span>
                  <button
                    type="button"
                    onClick={() => { setClientId(""); onClearInitialClient && onClearInitialClient(); }}
                    className="text-[var(--gis-green)] hover:text-[var(--gis-green-dark)] leading-none"
                    data-testid="ral-header-client-clear"
                    aria-label="Rimuovi filtro cliente"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </h2>
            <p className="text-[11px] text-slate-500 gis-body" data-testid="ral-summary">
              {loading ? "Caricamento…" : (
                <>
                  {totals.count} {totals.count === 1 ? "voce" : "voci"}
                  {totals.expensesAmount > 0 ? ` · spese € ${totals.expensesAmount.toFixed(2)}` : ""}
                  {(totals.billableCount ?? 0) > 0 ? (
                    <>
                      {" · "}
                      <span className="text-emerald-700 font-medium" data-testid="ral-billable-summary">
                        ✓ Fatturabili: {totals.billableCount}
                        {(totals.billableExpenses ?? 0) > 0 ? ` (€ ${(totals.billableExpenses ?? 0).toFixed(2)})` : ""}
                      </span>
                    </>
                  ) : ""}
                  {hasActiveFilters ? " (filtrate)" : ""}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="ral-print-btn" className="h-9"
                  title={`Stampa ${groupBy === "client" ? visibleGroups.length : visibleItems.length} righe filtrate`}>
            <Printer className="h-4 w-4 mr-1.5" /> Stampa
            <span className="ml-1 text-xs opacity-70">({groupBy === "client" ? visibleGroups.length : visibleItems.length})</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="ral-export-excel-btn" className="h-9"
                  title={`Esporta ${groupBy === "client" ? visibleGroups.length : visibleItems.length} righe filtrate`}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Esporta Excel
            <span className="ml-1 text-xs opacity-70">({groupBy === "client" ? visibleGroups.length : visibleItems.length})</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-3 items-end">
          {/* ═════════════════════════════════════════════════════════
              CLIENTE: filtro rimosso completamente (06/07/2026 — post
              secondo test reale). Nessun chip, nessun autocomplete.
              L'individuazione avviene via:
               1) ricerca globale (input "Cerca");
               2) deep-link dalla scheda cliente in Anagrafica → il
                  clientId viaggia in stato ma si vede solo come
                  indicatore compatto nell'header (rimovibile con ×).
              ═════════════════════════════════════════════════════════ */}

          {/* Periodo — Anno + Mese (Cliente 360° 01/07/2026) o Personalizzato */}
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Anno
            </label>
            <Select value={filterYear} onValueChange={setFilterYear} disabled={filterMode !== "month"}>
              <SelectTrigger className="w-[90px] h-9" data-testid="ral-period-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Mese
            </label>
            <Select value={filterMonth} onValueChange={setFilterMonth} disabled={filterMode !== "month"}>
              <SelectTrigger className="w-[120px] h-9" data-testid="ral-period-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Modalità
            </label>
            <Select
              value={filterMode}
              onValueChange={(v) => {
                setFilterMode(v);
                if (v === "custom") {
                  setFilterFrom(""); setFilterTo(""); setFilterPeriod("custom");
                }
              }}
            >
              <SelectTrigger className="w-[150px] h-9" data-testid="ral-period-mode">
                <CalendarDays className="h-3.5 w-3.5 mr-1.5 text-gray-400" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Mese/Anno</SelectItem>
                <SelectItem value="custom">Personalizzato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Azioni rapide periodo (Cliente 360° micro-refinement 01/07/2026) */}
          <div className="flex items-end gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 px-2 text-xs"
              onClick={() => {
                const t = todayISO();
                setFilterMode("custom");
                setFilterFrom(t);
                setFilterTo(t);
                setFilterPeriod("custom");
              }}
              data-testid="ral-period-quick-today"
              title="Filtra le sole attività di oggi"
            >
              Oggi
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 px-2 text-xs"
              onClick={() => {
                const now = new Date();
                setFilterMode("month");
                setFilterYear(String(now.getFullYear()));
                setFilterMonth(String(now.getMonth() + 1).padStart(2, "0"));
              }}
              data-testid="ral-period-quick-month"
              title="Filtra il mese corrente"
            >
              Mese corrente
            </Button>
          </div>
          {filterMode === "custom" && (
            <>
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">Dal</label>
                <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="w-[130px] h-9 text-sm" data-testid="ral-period-from" />
              </div>
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">Al</label>
                <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="w-[130px] h-9 text-sm" data-testid="ral-period-to" />
              </div>
            </>
          )}

          {/* Sorgenti */}
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Sorgenti
            </label>
            <div className="flex gap-1.5 h-9 items-center">
              <button
                type="button"
                onClick={() => setSourceLavoro((v) => !v)}
                className={`h-7 px-2.5 text-xs rounded-md border flex items-center gap-1 ${
                  sourceLavoro
                    ? "border-blue-300 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-400"
                }`}
                data-testid="ral-source-lavoro"
                title="Includi voci da Lavoro Quotidiano"
              >
                <ClipboardList className="h-3 w-3" /> Lavoro
              </button>
              <button
                type="button"
                onClick={() => setSourceDiario((v) => !v)}
                className={`h-7 px-2.5 text-xs rounded-md border flex items-center gap-1 ${
                  sourceDiario
                    ? "border-amber-300 bg-amber-50 text-amber-700"
                    : "border-slate-200 bg-white text-slate-400"
                }`}
                data-testid="ral-source-diario"
                title="Includi voci da Diario Giornaliero"
              >
                <BookOpen className="h-3 w-3" /> Diario
              </button>
              <button
                type="button"
                onClick={() => setSourceScadenze((v) => !v)}
                className={`h-7 px-2.5 text-xs rounded-md border flex items-center gap-1 ${
                  sourceScadenze
                    ? "border-violet-300 bg-violet-50 text-violet-700"
                    : "border-slate-200 bg-white text-slate-400"
                }`}
                data-testid="ral-source-scadenze"
                title="Includi voci dallo Scadenzario"
              >
                <CalendarDays className="h-3 w-3" /> Scadenze
              </button>
            </div>
          </div>

          {/* Vista */}
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Vista
            </label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[160px] h-9" data-testid="ral-group-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Dettaglio cronologico</SelectItem>
                <SelectItem value="client">
                  <span className="flex items-center gap-1.5">
                    <Users className="h-3 w-3" /> Panoramica per cliente
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Gestione — bloccata per operatori Studio/Agenzia: vedono
              solo la propria area (riflette il vincolo server-side). */}
          <div className="flex flex-col gap-0.5 min-w-[120px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Gestione
            </label>
            <Select
              value={filterMgmt || "__all__"}
              onValueChange={(v) => setFilterMgmt(v === "__all__" ? "" : v)}
              disabled={mgmtLocked}
            >
              <SelectTrigger
                className="h-9 text-sm w-[120px]"
                data-testid="ral-mgmt-filter"
                title={mgmtLocked ? "Filtro bloccato dal tuo ruolo operatore" : undefined}
              >
                <SelectValue placeholder="Tutte" />
              </SelectTrigger>
              <SelectContent>
                {!mgmtLocked && <SelectItem value="__all__">Tutte</SelectItem>}
                {(!mgmtLocked || forcedMgmt === "studio") && (
                  <SelectItem value="studio">Studio</SelectItem>
                )}
                {(!mgmtLocked || forcedMgmt === "agenzia") && (
                  <SelectItem value="agenzia">Agenzia</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Area (solo se Lavoro è abilitato — Diario usa contactType) */}
          <div className="flex flex-col gap-0.5 min-w-[130px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Area
            </label>
            <Select value={filterArea || "__all__"} onValueChange={(v) => setFilterArea(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm w-[130px]" data-testid="ral-area-filter"><SelectValue placeholder="Tutte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte</SelectItem>
                {AREA_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Operatore */}
          <div className="flex flex-col gap-0.5 min-w-[150px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Operatore
            </label>
            <Select value={filterOperator || "__all__"} onValueChange={(v) => setFilterOperator(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm w-[150px]" data-testid="ral-operator-filter"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                {operators.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.fullName || o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Stato */}
          <div className="flex flex-col gap-0.5 min-w-[160px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Stato
            </label>
            <Select value={filterStatus || "__all__"} onValueChange={(v) => setFilterStatus(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm w-[160px]" data-testid="ral-status-filter"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                <SelectItem value="chiusa">Chiuse (fatturabili)</SelectItem>
                <SelectItem value="aperta">Aperte (work-in-progress)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Ricerca testo libero */}
          <div className="flex-1 min-w-[200px] flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Cerca
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <DebouncedSearchInput
                placeholder="Cliente, descrizione, operatore..."
                externalValue={searchQuery}
                onDebounced={setSearchQuery}
                className="pl-9 h-9"
                testId="ral-search"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9 text-gray-500" data-testid="ral-reset-filters">
              <Filter className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>
      </Card>

      {/* Body */}
      {groupBy === "client" ? (
        <Card data-testid="ral-groups-card">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-700">Cliente</th>
                  <th className="text-right p-3 font-semibold text-slate-700">N. Attività</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Ultima</th>
                  <th className="text-right p-3 font-semibold text-slate-700">Spese Tot.</th>
                  <th className="text-right p-3 font-semibold text-slate-700">Pagate</th>
                  <th
                    className="text-right p-3 font-semibold text-slate-700 whitespace-nowrap"
                    title="Voci chiuse, pronte da fatturare. Le spese mostrate sono quelle complessive delle voci chiuse del cliente."
                  >
                    ✓ Fatturabili
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleGroups.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-slate-400">Nessuna attività con i filtri correnti.</td></tr>
                ) : pagedGroups.map((g) => (
                  <tr key={g.clientId || g.clientName} className="border-b hover:bg-slate-50" data-testid="ral-group-row">
                    <td className="p-3">
                      <button
                        type="button"
                        className="text-blue-700 hover:underline text-left"
                        onClick={() => { setClientId(g.clientId); setGroupBy("none"); }}
                        title="Vedi dettaglio cliente"
                      >
                        {g.clientName || "—"}
                      </button>
                    </td>
                    <td className="p-3 text-right">{g.count}</td>
                    <td className="p-3">{formatDateIT(g.lastActivityDate) || "—"}</td>
                    <td className="p-3 text-right">€ {g.expensesAmount.toFixed(2)}</td>
                    <td className="p-3 text-right text-emerald-700">€ {g.expensesPaid.toFixed(2)}</td>
                    <td className="p-3 text-right whitespace-nowrap" data-testid="ral-group-billable">
                      {(g.billableCount ?? 0) > 0 ? (
                        <span className="text-emerald-700 font-medium">
                          {g.billableCount}
                          {(g.billableExpenses ?? 0) > 0 ? ` · € ${(g.billableExpenses ?? 0).toFixed(2)}` : ""}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 pb-2">{groupsPaginationBar}</div>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="ral-items-card">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-700 whitespace-nowrap">Data</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Sorgente</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Cliente</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Area</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Operatore</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Stato</th>
                  <th className="text-left p-3 font-semibold text-slate-700">Descrizione</th>
                  <th className="text-right p-3 font-semibold text-slate-700 whitespace-nowrap">Spese</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-10 text-slate-400">Caricamento…</td></tr>
                ) : visibleItems.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-10 text-slate-400">Nessuna attività con i filtri correnti.</td></tr>
                ) : pagedItems.map((r) => {
                  // v1.9.1 — Timeline colorata per uniformità con la Vista 360°.
                  // Bar colorata a sinistra basata sulla sorgente della voce,
                  // così lo stream operativo è leggibile a colpo d'occhio.
                  const sourceBar = r.sourceType === "lavoro"    ? "#2563eb"    // blu
                                  : r.sourceType === "scadenza"  ? "#7c3aed"    // viola
                                  :                                 "#d97706";  // ambra (diario)
                  const sourceBg = r.sourceType === "lavoro"    ? "#dbeafe40"
                                 : r.sourceType === "scadenza"  ? "#ede9fe40"
                                 :                                 "#fef3c740";
                  return (
                  <tr
                    key={`${r.sourceType}-${r.id}`}
                    className="border-b hover:bg-slate-50 border-l-4"
                    style={{ borderLeftColor: sourceBar, backgroundColor: sourceBg }}
                    data-testid={`ral-row-${r.sourceType}-${r.id}`}
                  >
                    <td className="p-3 whitespace-nowrap">{formatDateIT(r.date)}</td>
                    <td className="p-3">
                      {r.sourceType === "lavoro" ? (
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 font-medium">
                          <ClipboardList className="h-3 w-3 mr-1" /> Lavoro
                        </Badge>
                      ) : r.sourceType === "scadenza" ? (
                        <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 font-medium">
                          <CalendarDays className="h-3 w-3 mr-1" /> Scadenza
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-medium">
                          <BookOpen className="h-3 w-3 mr-1" /> Diario
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 font-medium">{r.clientName || "—"}</td>
                    <td className="p-3">{getAreaLabel(r.area) || "—"}</td>
                    <td className="p-3 text-slate-600">{r.operatorName || "—"}</td>
                    <td className="p-3">
                      {r.status === "chiusa" ? (
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold"
                          style={{ backgroundColor: "#16a34a", color: "#fff" }}
                          data-testid="ral-row-billable"
                          title="Voce chiusa → fatturabile (anteprima del modulo 'Crea fattura da Registro')"
                        >
                          <span>Chiuso</span>
                          <span className="text-[9px] opacity-90">✓ Fatturabile</span>
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold"
                          style={{ backgroundColor: "#f1f5f9", color: "#64748b" }}
                        >
                          Aperto
                        </span>
                      )}
                    </td>
                    <td className="p-3 max-w-[420px] truncate" title={r.description}>
                      {r.description || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {r.expensesAmount ? (
                        <span className={r.expensesPaid ? "text-emerald-700" : "text-slate-700"}>
                          € {r.expensesAmount.toFixed(2)}{r.expensesPaid ? " ✓" : ""}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {/* v1.9.1 — Legenda barre colorate (uniformità con Vista 360°) */}
            <div className="px-3 py-2 border-t border-slate-100 flex items-center gap-4 text-[10px] gis-body text-slate-500 flex-wrap" data-testid="ral-color-legend">
              <span className="gis-heading-wide uppercase text-slate-500">Sorgente:</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: "#2563eb" }} />Lavoro</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: "#7c3aed" }} />Scadenza</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: "#d97706" }} />Diario</span>
              <span className="text-slate-300 mx-2">·</span>
              <span className="gis-heading-wide uppercase text-slate-500">Stato:</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: "#16a34a" }} />Chiuso · fatturabile</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: "#94a3b8" }} />Aperto</span>
            </div>
            <div className="px-3 pb-2">{itemsPaginationBar}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default React.memo(RegistroAttivitaTab);

